/* ==========================================================================
   <kott-gallery-strip> — horizontal scroll-snap strip of framed screenshots
   (sections/kott-project-gallery.liquid). Forked from the retired
   projects-deck.js; the DOM scrolls natively (touch + keyboard), this adds:

     - mouse drag-to-scroll on desktop
     - prev/next buttons (revealed only when JS runs; hidden on coarse pointers)
     - optional slow autoplay, paused on hover/focus and when offscreen
     - reduced motion via window.kottReduceMotion() + kott:reduce-motion-change
       (no autoplay, instant scrolls)

   RTL-safe: only signed scrollBy deltas, never absolute scrollLeft targets.
   ========================================================================== */
(function () {
  'use strict';

  if (window.customElements && customElements.get('kott-gallery-strip')) return;

  function isReduced() {
    return typeof window.kottReduceMotion === 'function' ? window.kottReduceMotion() : false;
  }

  class KottGalleryStrip extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-gallery-track]') || this;
      this.autoplayMs = parseInt(this.getAttribute('data-autoplay') || '0', 10) || 0;
      this.userPaused = false;
      this.inViewport = false;

      this.setupMouseDrag();
      this.setupHoverPause();
      this.setupButtons();
      this.setupVisibilityObserver();

      this.onReduce = (e) => { if (e && e.detail && e.detail.reduced) this.stopAutoplay(); else this.maybeStartAutoplay(); };
      window.addEventListener('kott:reduce-motion-change', this.onReduce);
    }

    disconnectedCallback() {
      this.stopAutoplay();
      if (this.io) this.io.disconnect();
      window.removeEventListener('kott:reduce-motion-change', this.onReduce);
    }

    behavior() { return isReduced() ? 'auto' : 'smooth'; }

    /* ---- Mouse drag-to-scroll (desktop only) ---- */
    setupMouseDrag() {
      const track = this.track;
      let isDown = false, startX = 0, startScroll = 0, moved = false;

      track.addEventListener('mousedown', (e) => {
        if (e.button !== 0) return;
        isDown = true; moved = false;
        startX = e.clientX; startScroll = track.scrollLeft;
        track.setAttribute('data-dragging', '');
        this.stopAutoplay();
      });
      track.addEventListener('mousemove', (e) => {
        if (!isDown) return;
        const dx = e.clientX - startX;
        if (Math.abs(dx) > 4) moved = true;
        track.scrollLeft = startScroll - dx;
      });
      const end = () => {
        if (!isDown) return;
        isDown = false;
        track.removeAttribute('data-dragging');
        this.maybeStartAutoplay();
        if (moved) {
          const swallow = (ev) => { ev.preventDefault(); ev.stopPropagation(); };
          track.addEventListener('click', swallow, { capture: true, once: true });
          setTimeout(() => track.removeEventListener('click', swallow, true), 50);
        }
      };
      track.addEventListener('mouseup', end);
      track.addEventListener('mouseleave', end);
    }

    /* ---- Hover / focus pause ---- */
    setupHoverPause() {
      this.addEventListener('mouseenter', () => { this.userPaused = true; this.stopAutoplay(); });
      this.addEventListener('mouseleave', () => { this.userPaused = false; this.maybeStartAutoplay(); });
      this.addEventListener('focusin', () => { this.userPaused = true; this.stopAutoplay(); });
      this.addEventListener('focusout', () => { this.userPaused = false; this.maybeStartAutoplay(); });
    }

    /* ---- Prev / next ---- */
    setupButtons() {
      const nav = this.querySelector('[data-gallery-nav]');
      if (!nav) return;
      const fine = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
      if (!fine) return;
      nav.hidden = false;
      nav.querySelectorAll('[data-dir]').forEach((btn) => {
        btn.addEventListener('click', () => {
          this.stopAutoplay();
          this.step(btn.getAttribute('data-dir') === 'next' ? 1 : -1);
        });
      });
    }

    stepWidth() {
      const cards = this.track.querySelectorAll('[data-gallery-shot]');
      if (!cards.length) return this.track.clientWidth * 0.8;
      return cards[0].getBoundingClientRect().width + parseFloat(getComputedStyle(this.track).columnGap || '0');
    }

    /* dir: +1 = forward in reading direction, -1 = back */
    step(dir) {
      const rtl = getComputedStyle(this.track).direction === 'rtl';
      const delta = (rtl ? -1 : 1) * dir * this.stepWidth();
      this.track.scrollBy({ left: delta, behavior: this.behavior() });
    }

    /* ---- Autoplay ---- */
    maybeStartAutoplay() {
      if (isReduced() || !this.autoplayMs || this.userPaused || !this.inViewport || this.timer) return;
      this.timer = setInterval(() => this.advance(), this.autoplayMs);
    }
    stopAutoplay() {
      if (this.timer) { clearInterval(this.timer); this.timer = null; }
    }
    advance() {
      const track = this.track;
      const max = track.scrollWidth - track.clientWidth;
      if (Math.abs(track.scrollLeft) >= max - 8) {
        track.scrollTo({ left: 0, behavior: this.behavior() });
        return;
      }
      this.step(1);
    }

    /* ---- Visibility ---- */
    setupVisibilityObserver() {
      if (typeof IntersectionObserver === 'undefined') { this.inViewport = true; this.maybeStartAutoplay(); return; }
      this.io = new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          this.inViewport = e.isIntersecting;
          if (this.inViewport) this.maybeStartAutoplay(); else this.stopAutoplay();
        });
      }, { threshold: 0.25 });
      this.io.observe(this);
    }
  }

  customElements.define('kott-gallery-strip', KottGalleryStrip);
})();
